export const AUTOPLAY_INTERVAL_MS = 6000;

export type CarouselTimers = {
  clearInterval: (id: number) => void;
  setInterval: (callback: () => void, delay: number) => number;
};

/**
 * The carousel's behaviour, as the React section ran it: the arrows and dots scroll the track a slide
 * at a time, the dot of the slide in view is marked, and the slides advance on their own every six
 * seconds unless the reader hovers or focuses inside the section.
 */
export const delegateProductsCarousel = (document: Document, timers: CarouselTimers = { clearInterval: id => window.clearInterval(id), setInterval: (callback, delay) => window.setInterval(callback, delay) }) => {
  const section = document.querySelector<HTMLElement>('[data-testid="products-carousel-section"]');
  const track = section?.querySelector<HTMLElement>('.landing-carousel-track');
  if (!section || !track) return;
  const dots = [...section.querySelectorAll<HTMLButtonElement>('.landing-carousel-dot')];
  const count = dots.length;
  let active = 0;
  let paused = false;
  let timer: number | undefined;

  const scrollToIndex = (index: number) => {
    const wrapped = (index + count) % count;
    track.scrollTo({ behavior: 'smooth', left: wrapped * track.clientWidth });
  };

  const restart = () => {
    if (timer !== undefined) timers.clearInterval(timer);
    timer = paused ? undefined : timers.setInterval(() => scrollToIndex(active + 1), AUTOPLAY_INTERVAL_MS);
  };

  const mark = (index: number) => {
    if (index === active) return;
    active = index;
    dots.forEach((dot, position) => {
      if (position === active) dot.setAttribute('data-active', 'true');
      else dot.removeAttribute('data-active');
    });
    restart();
  };

  track.addEventListener('scroll', () => {
    if (track.clientWidth === 0) return;
    mark(Math.round(track.scrollLeft / track.clientWidth));
  });
  section.querySelector('[data-testid="carousel-prev"]')?.addEventListener('click', () => scrollToIndex(active - 1));
  section.querySelector('[data-testid="carousel-next"]')?.addEventListener('click', () => scrollToIndex(active + 1));
  dots.forEach((dot, index) => dot.addEventListener('click', () => scrollToIndex(index)));

  const pause = () => {
    paused = true;
    restart();
  };
  const resume = () => {
    paused = false;
    restart();
  };
  section.addEventListener('mouseenter', pause);
  section.addEventListener('mouseleave', resume);
  section.addEventListener('focusin', pause);
  section.addEventListener('focusout', resume);
  restart();
};
